import { apiClient } from '../../../api/client'
import { ItemsfeedbackFormApi } from '../../../generated-types/apis/ItemsfeedbackFormApi'
import {
  type APIFeedbackFormRequest,
  APIFeedbackFormRequestToJSON,
  type APIPatchedFeedbackFormRequest,
} from '../../../generated-types/models'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { createFeedbackForm } from '../../Item/utils/functions'
import { type FeedbackForm } from '../../Item/utils/types'
import { inputToAPIFeedbackFormJSON } from '../../Item/api/articleApi'

const log = new LogTool({ context: 'FormEditorAPI', enable: true, logLevel: 'warn' })

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Create API instance
 */
const feedbackFormsApi = new ItemsfeedbackFormApi(getApiConfiguration())

/**
 * Create a feedback form
 */
export async function createFeedbackFormApi(
  formData: APIFeedbackFormRequest | any
): Promise<FeedbackForm> {
  log.info('Creating feedback form:', formData)

  // Convert input to API format if needed
  const apiRequest = 'formTitle' in formData && 'formJson' in formData && 
                     Array.isArray(formData.formJson) && 
                     formData.formJson.length > 0 &&
                     typeof formData.formJson[0] === 'object' &&
                     'key' in formData.formJson[0]
    ? inputToAPIFeedbackFormJSON(formData)
    : formData as APIFeedbackFormRequest

  const response = await feedbackFormsApi.v1ItemsFeedbackFormsCreate({
    aPIFeedbackFormRequest: apiRequest,
  })

  return createFeedbackForm(response as any)
}

/**
 * Update a feedback form
 */
export async function updateFeedbackFormApi(
  idOrSelf: string | number,
  formData: APIFeedbackFormRequest | any
): Promise<FeedbackForm> {
  log.info('Updating feedback form:', idOrSelf, formData)

  // Convert input to API format if needed
  const apiRequest = 'formTitle' in formData && 'formJson' in formData && 
                     Array.isArray(formData.formJson) && 
                     formData.formJson.length > 0 &&
                     typeof formData.formJson[0] === 'object' &&
                     'key' in formData.formJson[0]
    ? inputToAPIFeedbackFormJSON(formData)
    : formData as APIFeedbackFormRequest

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await feedbackFormsApi.v1ItemsFeedbackFormsUpdate({
      id: String(idOrSelf),
      aPIFeedbackFormRequest: apiRequest,
    })
    return createFeedbackForm(response as any)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/items/feedback_forms/${idOrSelf}/`
  const json = APIFeedbackFormRequestToJSON(apiRequest)
  const response = await apiClient.put<any>(url, json)
  return createFeedbackForm(response)
}

/**
 * Fetch a single feedback form by ID or URL
 */
export async function fetchFeedbackFormApi(id: string | number): Promise<FeedbackForm> {
  log.info('Fetching feedback form:', id)

  // If it's a numeric ID, use generated API
  if (typeof id === 'number') {
    const response = await feedbackFormsApi.v1ItemsFeedbackFormsRetrieve({
      id: String(id),
    })
    return createFeedbackForm(response as any)
  }

  // For URL-based fetches, use apiClient
  const url = id.includes('/') ? id : `v1/items/feedback_forms/${id}/`
  const response = await apiClient.get<any>(url)
  return createFeedbackForm(response)
}

/**
 * Fetch all feedback forms
 */
export async function fetchFeedbackFormsApi(): Promise<FeedbackForm[]> {
  log.info('Fetching all feedback forms')

  const response = await feedbackFormsApi.v1ItemsFeedbackFormsList({})
  return response.results ? response.results.map((form: any) => createFeedbackForm(form)) : []
}

/**
 * Delete a feedback form
 */
export async function deleteFeedbackFormApi(id: string | number): Promise<void> {
  log.info('Deleting feedback form:', id)

  // If it's a numeric ID, use generated API
  if (typeof id === 'number') {
    await feedbackFormsApi.v1ItemsFeedbackFormsDestroy({ id: String(id) })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = id.includes('/') ? id : `v1/items/feedback_forms/${id}/`
  await apiClient.delete(url)
}

